/**
 * Connection Wizard Component
 * Multi-step modal for adding new affiliate networks
 */

import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useNetworksStore } from '../../stores/networksStore';
import { useCreateNetwork } from '../../hooks/useNetworksData';
import { SelectPlatform } from './wizard/SelectPlatform';
import { EnterCredentials } from './wizard/EnterCredentials';
import { Configuration } from './wizard/Configuration';
import type { Step1Data, Step2Data, Step3Data, NewNetworkData } from '../../types/networks.types';
import { toast } from 'sonner';

const STEPS = [
  { step: 1, title: 'Select Platform', description: 'Choose your affiliate network platform' },
  { step: 2, title: 'Enter Credentials', description: 'Connect your account' },
  { step: 3, title: 'Configuration', description: 'Configure sync settings' }
];

export const ConnectionWizard: React.FC = () => {
  const { wizardOpen, wizardStep, closeWizard, nextStep, prevStep, resetWizard } = useNetworksStore();
  const createMutation = useCreateNetwork();

  // Form data state
  const [step1Data, setStep1Data] = useState<Step1Data>({
    platform_provider_id: '',
    platform_name: '',
    platform_logo: null,
    auth_method: undefined,
    required_credentials: []
  });

  const [step2Data, setStep2Data] = useState<Step2Data>({
    credentials: {},
    login_url: '',
    username: undefined
  });

  const [step3Data, setStep3Data] = useState<Step3Data>({
    account_name: '',
    sync_frequency: 60,
    commission_model: 'revshare',
    notes: '',
    account_type: 'direct',
    parent_network_id: null
  });

  const handleClose = () => {
    if (createMutation.isPending) return;
    
    // Reset form data
    setStep1Data({
      platform_provider_id: '',
      platform_name: '',
      platform_logo: null,
      auth_method: undefined,
      required_credentials: []
    });
    setStep2Data({
      credentials: {},
      login_url: '',
      username: undefined
    });
    setStep3Data({
      account_name: '',
      sync_frequency: 60,
      commission_model: 'revshare',
      notes: '',
      account_type: 'direct',
      parent_network_id: null
    });
    
    resetWizard();
  };

  const canProceedToStep2 = () => {
    return step1Data.platform_provider_id !== '';
  };

  const canProceedToStep3 = () => {
    if (!step2Data.login_url) return false;
    
    if (step1Data.auth_method === 'username_password') {
      return step2Data.credentials.username && step2Data.credentials.password;
    } else if (step1Data.auth_method === 'api_key') {
      const hasApiKey = step2Data.credentials.api_key;
      const needsSecret = step1Data.required_credentials?.includes('api_secret');
      return hasApiKey && (!needsSecret || step2Data.credentials.api_secret);
    }
    
    return false;
  };

  const canSave = () => {
    return step3Data.account_name.trim() !== '';
  };

  const handleNext = () => {
    if (wizardStep === 1 && canProceedToStep2()) {
      nextStep();
    } else if (wizardStep === 2 && canProceedToStep3()) {
      nextStep();
    }
  };

  const handleSave = async () => {
    if (!canSave()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const networkData: NewNetworkData = {
        ...step1Data,
        ...step2Data,
        ...step3Data
      };

      await createMutation.mutateAsync(networkData);
      toast.success('Network added successfully!');
      handleClose();
    } catch (error) {
      toast.error('Failed to create network');
    }
  };

  return (
    <Dialog.Root open={wizardOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 max-h-[90vh] w-[90vw] max-w-3xl translate-x-[-50%] translate-y-[-50%] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Add Network Connection
                </Dialog.Title>
                <Dialog.Description className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {STEPS[wizardStep - 1].description}
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded p-1 transition-colors duration-200"
                  aria-label="Close"
                  disabled={createMutation.isPending}
                >
                  <X className="w-5 h-5" />
                </button>
              </Dialog.Close>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {STEPS.map((step, index) => (
                <React.Fragment key={step.step}>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                        wizardStep === step.step
                          ? 'bg-blue-600 text-white'
                          : wizardStep > step.step
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {step.step}
                    </div>
                    <span
                      className={`text-sm font-medium ${
                        wizardStep === step.step
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div className="w-12 h-0.5 bg-gray-200 dark:bg-gray-700" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 overflow-y-auto max-h-[60vh]">
            {wizardStep === 1 && (
              <SelectPlatform data={step1Data} onChange={setStep1Data} />
            )}
            {wizardStep === 2 && (
              <EnterCredentials
                platformData={step1Data}
                data={step2Data}
                onChange={setStep2Data}
              />
            )}
            {wizardStep === 3 && (
              <Configuration
                platformData={step1Data}
                credentialsData={step2Data}
                data={step3Data}
                onChange={setStep3Data}
              />
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={wizardStep === 1 || createMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            <div className="flex gap-2">
              {wizardStep < 3 ? (
                <button
                  onClick={handleNext}
                  disabled={
                    (wizardStep === 1 && !canProceedToStep2()) ||
                    (wizardStep === 2 && !canProceedToStep3()) ||
                    createMutation.isPending
                  }
                  className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  disabled={!canSave() || createMutation.isPending}
                  className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save & Connect'
                  )}
                </button>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
